/**
 * Staking and custody pools.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { EmptyState } from '../components/ui/EmptyState';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedFixedTopBar, ThemedInput, ThemedPanel } from '../components/ui/ThemedSurface';
import { STAKING_COLORS as C, STAKING_POOLS, STAKING_TABS, type StakePoolDef } from '@/constants';

interface BalancesResponse {
  balances: Record<string, string>;
}

function TabStrip({ value, onChange }: { value: 'pools' | 'mine'; onChange: (value: 'pools' | 'mine') => void }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-1">
      {STAKING_TABS.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-9 overflow-hidden rounded-xl px-2 text-xs font-semibold transition-all active:scale-[0.98]"
            style={{
              color: active ? C.gold : C.muted,
            }}
          >
            {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
            <span className="relative block truncate">{t(tab.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

function StatsBanner() {
  const { t } = useTranslation();
  const items = [
    { value: String(STAKING_POOLS.length), label: t('staking.stats.pools'), color: C.gold },
    { value: '22%', label: t('staking.stats.maxApy'), color: C.blue },
    { value: t('staking.stats.active'), label: t('staking.stats.status'), color: C.buy },
  ];

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {items.map((item) => (
          <div key={item.label} className="relative min-w-0 overflow-hidden rounded-xl border px-2 py-2.5 text-center" style={{ borderColor: `${item.color}3d`, background: `linear-gradient(180deg, ${item.color}14 0%, rgba(0,0,0,0.24) 100%)` }}>
            <div className="absolute inset-x-4 top-0 h-px" style={{ background: item.color }} />
            <div className="truncate text-lg font-bold leading-none" style={{ color: item.color, textShadow: `0 0 10px ${item.color}55` }}>
              {item.value}
            </div>
            <div className="mt-1.5 truncate text-[10px] leading-none" style={{ color: C.muted }}>{item.label}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function PoolCard({
  pool,
  balance,
  selected,
  amount,
  onSelect,
  onAmountChange,
}: {
  pool: StakePoolDef;
  balance: string;
  selected: boolean;
  amount: string;
  onSelect: () => void;
  onAmountChange: (amount: string) => void;
}) {
  const { t } = useTranslation();
  const projected = selected && Number(amount) > 0 ? ((Number(amount) * Number(pool.apy)) / 100 / 365).toFixed(6) : null;

  return (
    <ThemedPanel className="p-0" theme={C}>
      <div className="absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${pool.color}88 50%, transparent 100%)` }} />
      <div className="relative z-10 flex flex-col gap-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid size-11 shrink-0 place-items-center rounded-xl border text-xs font-bold" style={{ borderColor: `${pool.color}66`, background: `linear-gradient(180deg, ${pool.color}20 0%, rgba(0,0,0,0.20) 100%)`, color: pool.color, boxShadow: `0 0 14px ${pool.color}33` }}>
              {pool.token.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold" style={{ color: C.text }}>{t(pool.labelKey)}</div>
              <div className="mt-2 flex min-w-0 flex-wrap gap-1.5">
                <span className="rounded-full border px-2 py-0.5 text-[10px]" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
                  {pool.lockDays === 0 ? t('staking.noLock') : t('staking.lockDays', { days: pool.lockDays })}
                </span>
                <span className="rounded-full border px-2 py-0.5 text-[10px]" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
                  {t('staking.minimum')} {pool.minAmount}
                </span>
              </div>
            </div>
          </div>
          <div className="shrink-0 rounded-xl border px-3 py-2 text-right" style={{ borderColor: `${pool.color}44`, background: `${pool.color}12` }}>
            <div className="text-xl font-bold leading-none" style={{ color: pool.color }}>{pool.apy}%</div>
            <div className="mt-1 text-[10px]" style={{ color: C.muted }}>{t('staking.annualYield')}</div>
          </div>
        </div>

        <div className="h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold}44 16%, ${C.gold}44 84%, transparent 100%)` }} />

        <div className="grid grid-cols-[1fr_12px_1fr] items-stretch">
          <Metric label={t('staking.totalStaked')} value={`${pool.totalStaked} ${pool.token.toUpperCase()}`} />
          <div className="mx-auto w-px self-stretch" style={{ background: `linear-gradient(180deg, transparent 0%, ${C.gold}55 18%, ${C.gold}55 82%, transparent 100%)` }} />
          <Metric label={t('staking.holding')} value={`${balance} ${pool.token.toUpperCase()}`} />
        </div>
        
        <div className="relative">
          <ThemedInput type="number" placeholder={t('common.amount')} value={selected ? amount : ''} onFocus={onSelect} onChange={(e) => { onSelect(); onAmountChange(e.target.value); }} min="0" step="any" />
          <button type="button" disabled={!selected || !amount} className="absolute right-2 top-1/2 flex h-7 -translate-y-1/2 items-center rounded-md border px-3 text-[11px] font-semibold transition hover:brightness-110 disabled:opacity-50 disabled:brightness-75" style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text }}>
            {t('staking.stake')}
          </button>
        </div>

        {projected && (
          <div className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: `${C.buy}44`, background: `${C.buy}12`, color: C.muted }}>
            {t('staking.estimatedDailyReturn')}: <span className="font-mono" style={{ color: C.buy }}>{projected} {pool.token.toUpperCase()}</span>
          </div>
        )}
      </div>
    </ThemedPanel>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-xl border px-3 py-2" style={{ borderColor: C.line, background: C.inputBg }}>
      <div className="truncate text-[10px]" style={{ color: C.muted }}>{label}</div>
      <div className="mt-1 truncate font-mono text-xs font-semibold" style={{ color: C.text }}>{value}</div>
    </div>
  );
}

export function Staking() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedPool, setSelectedPool] = useState<StakePoolDef | null>(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [tab, setTab] = useState<'pools' | 'mine'>('pools');

  const { data: bal } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
  });

  const balances = bal?.balances ?? {};

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('staking.title')} onBack={() => navigate(-1)} theme={C} />

        <ThemedFixedTopBar background={C.topbarBg} className="flex flex-col gap-2 p-2">
          <StatsBanner />
          <TabStrip value={tab} onChange={setTab} />
        </ThemedFixedTopBar>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[180px]" style={{ height: 'calc(100% - 180px)', paddingBottom: '12px' }}>
          {tab === 'pools' && (
            <div className="flex flex-col gap-3">
              {STAKING_POOLS.map((pool) => (
                <PoolCard
                  key={pool.id}
                  pool={pool}
                  balance={balances[pool.token] ?? '0'}
                  selected={selectedPool?.id === pool.id}
                  amount={stakeAmount}
                  onSelect={() => setSelectedPool(pool)}
                  onAmountChange={setStakeAmount}
                />
              ))}
            </div>
          )}

          {tab === 'mine' && <EmptyState title={t('staking.noRecords')} description={t('staking.emptyDesc')} />}
        </div>
      </div>
    </div>
  );
}
